import React, { useState, useEffect } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { Button, Collapse, Modal } from "antd";

import Loading from "../../../Common/Loading";
import NoResultsGIF from "../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";

import { API_URL } from "../../../../utils/Constant";
import { formatDateForDisplay } from "../../../../utils/helpers";

import pumpkinGIF from "../../../../assets/images/environment/pumpkin.gif";
import { ReactComponent as EditOutlineIcon } from "../../../../assets/images/supply-pickup/EditOutline.svg";

import "./ViewRecurringRequestModal.css";

const getItemImageSrc = (item) => {
  if (item.image_url) return item.image_url;
  const { image_path: path } = item;
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_URL}/${path.replace(/^\//, "")}`;
};

const formatFrequencyLabel = (frequency) => {
  // if (frequency === "weekly") return "Weekly";
  if (frequency === "monthly") return "Monthly";
  return "-";
};

const RecurringRequestDateSummary = ({
  request, onEditClick, onCancelClick, editingRequestId, cancellingRequestId,
}) => {
  const { areResultsLocked } = useAccessStatus();
  const { start_date: startDate, end_date: endDate } = request;

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (areResultsLocked || !onEditClick) {
      return;
    }
    onEditClick(request);
  };

  const handleCancelClick = (e) => {
    e.stopPropagation();
    if (onCancelClick) {
      onCancelClick(request);
    }
  };

  return (
    <div className="ViewRecurringRequestModal__DateSummary">
      <div className="ViewRecurringRequestModal__DateSummaryItem">
        <label>Start Date</label>
        <span>{startDate ? formatDateForDisplay(startDate, "DD MMM YYYY") : "-"}</span>
      </div>
      <div className="ViewRecurringRequestModal__DateSummaryItem">
        <label>End Date</label>
        <span>{endDate ? formatDateForDisplay(endDate, "DD MMM YYYY") : "-"}</span>
      </div>
      <div className="ViewRecurringRequestModal__DateSummaryItem">
        <label>Frequency</label>
        <span>{formatFrequencyLabel(request?.frequency)}</span>
      </div>
      <div className="ViewRecurringRequestModal__DateSummaryActions">
        <label>Actions</label>
        <div className="ViewRecurringRequestModal__ActionBtns">
          <StyledTooltip title={areResultsLocked ? "Editing is locked until payment is completed" : "Edit"} placement="top">
            <span>
              <Button
                type="text"
                className="ViewRecurringRequestModal__EditBtn"
                icon={<EditOutlineIcon />}
                loading={editingRequestId === request.supply_request_id}
                disabled={areResultsLocked}
                onClick={handleEditClick}
              />
            </span>
          </StyledTooltip>
          <StyledTooltip title="Cancel" placement="top">
            <Button
              type="text"
              className="ViewRecurringRequestModal__CancelBtn"
              icon={<CloseOutlined />}
              loading={cancellingRequestId === request.supply_request_id}
              onClick={handleCancelClick}
            />
          </StyledTooltip>
        </div>
      </div>
    </div>
  );
};

const RecurringRequestEntry = ({ request }) => {
  const dates = request?.dates ?? [];
  const items = request?.items ?? [];
  const itemCount = request.total_requested_items ?? items.length;
  const itemsTitle = itemCount === 1 ? "Item Requested" : "Items Requested";

  return (
    <div className="ViewRecurringRequestModal__EntryContent">
      <div className="ViewRecurringRequestModal__PanelsWrapper">
        <div className="ViewRecurringRequestModal__Panels">
          <div className="ViewRecurringRequestModal__ItemsPanel">
            <div className="ViewRecurringRequestModal__PanelHeader">
              <div className="ViewRecurringRequestModal__HeaderAccent" aria-hidden />
              <h5>
                {itemsTitle}
                {" ("}
                {itemCount}
                )
              </h5>
            </div>
            <StyledScrollbar className="SupplyInventoryItemsScrollbar ViewRecurringRequestModal__Scrollbar ViewRecurringRequestModal__Scrollbar--items">
              <div className="ViewRecurringRequestModal__ItemsList">
                {items.map((item, index) => {
                  const imageSrc = getItemImageSrc(item);
                  return (
                    <div key={`${item.inventory_item_id}_${index}`} className="ViewRecurringRequestModal__ItemCard">
                      <div className="ViewRecurringRequestModal__ItemImage">
                        {imageSrc && <img src={imageSrc} alt={item.item_name} />}
                        {!imageSrc && <span>No Image</span>}
                      </div>
                      <span className="ViewRecurringRequestModal__ItemName">
                        {item.item_name}
                        {" ("}
                        {item.quantity}
                        )
                      </span>
                    </div>
                  );
                })}
                {items.length === 0 && (
                <span className="ViewRecurringRequestModal__EmptyText">-</span>
                )}
              </div>
            </StyledScrollbar>
          </div>

          <div className="ViewRecurringRequestModal__DatesPanel">
            <h6>Upcoming supply request dates</h6>
            <StyledScrollbar className="SupplyInventoryItemsScrollbar ViewRecurringRequestModal__Scrollbar ViewRecurringRequestModal__Scrollbar--dates">
              <div className="ViewRecurringRequestModal__DatesGrid">
                {dates.map((date, dateIndex) => (
                  <span className="ViewRecurringRequestModal__DateChip" key={dateIndex}>
                    {formatDateForDisplay(date, "DD MMM YYYY")}
                  </span>
                ))}
                {dates.length === 0 && (
                <span className="ViewRecurringRequestModal__DateChip ViewRecurringRequestModal__DateChip--empty">
                  -
                </span>
                )}
              </div>
            </StyledScrollbar>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewRecurringRequestModal = ({
  open: parentOpen,
  onClose,
  data: parentData,
  paginationInfo = { nextPageLink: "", currentPage: 1, totalPages: 1 },
  fetchRecurringRequests,
  loadingNewContent = false,
  onEditClick,
  onCancelClick,
}) => {
  const { areResultsLocked } = useAccessStatus();
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState([]);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const requests = Array.isArray(parentData) ? parentData : [];

  const handleCancel = () => {
    setOpen(false);
    setActiveKey([]);
    setEditingRequestId(null);
    setCancellingRequestId(null);
    onClose();
  };

  const handleEditClick = async (request) => {
    if (areResultsLocked || !onEditClick) return;
    setEditingRequestId(request.supply_request_id);
    try {
      await onEditClick(request);
    } finally {
      setEditingRequestId(null);
    }
  };

  const handleCancelClick = async (request) => {
    if (!onCancelClick) return;
    setCancellingRequestId(request.supply_request_id);
    try {
      await onCancelClick(request);
    } finally {
      setCancellingRequestId(null);
    }
  };

  const handleScrollToBottom = (e) => {
    if (!fetchRecurringRequests || loadingNewContent) return;

    const { nextPageLink, hasMore } = paginationInfo;
    if (!nextPageLink || hasMore === false) return;

    const tolerance = 3;
    const bottom = Math.abs(Math.ceil(e.contentScrollHeight - e.scrollTop) - Math.ceil(e.clientHeight)) <= tolerance;
    if (!bottom) return;

    const nextPage = nextPageLink.split("page=")[1]?.split("&")[0];
    if (!nextPage) return;

    fetchRecurringRequests(nextPage);
  };

  useEffect(() => {
    if (parentOpen) {
      setOpen(parentOpen);
      if (requests.length > 0) {
        setActiveKey(requests.map((request) => request.supply_request_id));
      }
    }
  }, [parentOpen, parentData]); // eslint-disable-line

  const accordionItems = requests.map((request) => ({
    key: request.supply_request_id,
    label: (
      <RecurringRequestDateSummary
        request={request}
        onEditClick={handleEditClick}
        onCancelClick={handleCancelClick}
        editingRequestId={editingRequestId}
        cancellingRequestId={cancellingRequestId}
      />
    ),
    children: (
      <RecurringRequestEntry request={request} />
    ),
  }));

  return (
    <Modal
      centered
      maskClosable
      destroyOnClose
      title={(
        <div className="ViewRecurringRequestModal__Title">
          <div className="ViewRecurringRequestModal__TitleAccent" aria-hidden />
          <span>Recurring Supply Request</span>
        </div>
      )}
      className="ViewRecurringRequestModal"
      open={open}
      onCancel={handleCancel}
      footer={null}
    >
      <div className="ViewRecurringRequestModal__ScrollContainer">
        <StyledScrollbar
          className="SupplyInventoryItemsScrollbar ViewRecurringRequestModal__Scrollbar ViewRecurringRequestModal__Scrollbar--entries"
          translateContentSizeYToHolder
          onScrollStop={(e) => handleScrollToBottom(e)}
        >
          {requests.length > 0 ? (
            <Collapse
              activeKey={activeKey}
              onChange={setActiveKey}
              className="ViewRecurringRequestModal__Accordion"
              items={accordionItems}
            />
          ) : (
            <NoResultsGIF
              image={pumpkinGIF}
              message="No Data"
              className="ViewRecurringRequestModal__NoResults"
            />
          )}
          {loadingNewContent && requests.length > 0 && (
            <div className="ViewRecurringRequestModal__LoadingMore">
              <Loading />
            </div>
          )}
        </StyledScrollbar>
      </div>
    </Modal>
  );
};

export default ViewRecurringRequestModal;
